import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Timeline, type Zoom } from '@/components/roadmap/timeline'
import { ViewHeader } from '@/components/shell/view-header'
import { useViewFilters } from '@/hooks/use-view-filters'
import { fieldByName, useIssueFields, useIssues } from '@/lib/query'
import { ViewError } from '@/components/common/query-states'
import { ROADMAP_VIEW } from '@/lib/views'

function Legend() {
  return (
    <span className="text-faint-foreground flex items-center gap-3 text-[11px]">
      <span className="flex items-center gap-1">
        <span className="h-2 w-3 rounded-sm bg-[var(--st-done)]" /> Live
      </span>
      <span className="flex items-center gap-1">
        <span className="bg-border-strong h-2 w-3 rounded-sm" /> Committed target
      </span>
      <span className="flex items-center gap-1">
        <span className="border-border-strong h-2 w-3 rounded-sm border border-dashed" /> Scheduled
      </span>
    </span>
  )
}

export default function Roadmap() {
  const { data: issues, isPending, isError, error, refetch } = useIssues()
  const fieldsQuery = useIssueFields()
  const fields = fieldsQuery.data
  const chipFilter = useViewFilters()
  const [searchParams, setSearchParams] = useSearchParams()

  const zoomParam = searchParams.get('zoom')
  const zoom: Zoom = zoomParam === 'month' || zoomParam === 'week' ? zoomParam : 'quarter'

  const features = useMemo(
    () =>
      chipFilter.apply(ROADMAP_VIEW.pool(issues ?? [], {})),
    [issues, chipFilter],
  )

  const quarterOptions = useMemo(
    () => fieldByName(fields, 'Quarter')?.options.map((o) => o.name) ?? [],
    [fields],
  )

  const toolbar = (
    <div className="flex items-center gap-3">
      <Legend />
      <Select
        value={zoom}
        onValueChange={(v) =>
          setSearchParams((p) => {
            const next = new URLSearchParams(p)
            next.set('zoom', v)
            return next
          }, { replace: true })
        }
      >
        <SelectTrigger size="sm" className="h-6 w-24 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="quarter">Quarter</SelectItem>
          <SelectItem value="month">Month</SelectItem>
          <SelectItem value="week">Week</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title="Roadmap"
        meta={
          <span className="font-mono text-[11px]">type:Feature -stage:Done · {features.length} items</span>
        }
        toolbar={toolbar}
      />
      <div className="min-h-0 flex-1">
        {isError || fieldsQuery.isError ? (
          <ViewError what="the roadmap" error={error ?? fieldsQuery.error} onRetry={() => { refetch(); fieldsQuery.refetch() }} />
        ) : isPending || !fields ? (
          <Skeleton className="m-4 h-[calc(100%-2rem)]" />
        ) : (
          <Timeline issues={features} quarterOptions={quarterOptions} zoom={zoom} />
        )}
      </div>
    </div>
  )
}
